import React, { useState, useEffect } from "react";
import * as dateFns from "date-fns";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import Modal from "../../common/Modal";
import Button from "../../common/FormFields/Button";
import Search from "../../common/FormFields/Search";
import CustomSelect from "../../common/FormFields/Select";
import OverWriteModal from "./OverWriteModal";
import {
  prepareTimeslotForUpdating,
  prepareTimeslotForCreation,
} from "./helpers";
import {
  timestampToIso,
} from "../../../services/helpers";

const BookAppointment = (props) => {
  const {
    selectedTimeslot,
    bloodBankId,
    bloodBankName,
    bookAppointmentModalIsOpen,
    toggleBookAppointmentModal,
    userByPnr,
    isOverwriting,
    overwritingError,
    getUserByPnr,
    bookAppointment,
    cancelAppointmentBooking,
    overwriteTimeslot,
    isOverwriteModalOpened,
    toggleOverwriteModal,
    appointmentTypes,
    recurrenceRule,
    startOverwriteTimeslot,
    finishOverwriteTimeslot,
    currentAppointment,
    isDayPage
  } = props;
  const { t } = useTranslation();
  const { date } = useParams();
  const [userPnr, setUserPnr] = useState("");
  const [currentUser, setCurrentUser] = useState(null);
  const [currentAppointments, setCurrentAppointments] = useState([]);
  const [chosenAppointment, setChosenAppointment] = useState(null);
  const [isTiterMatch, setIsTiterMatch] = useState(false);
  const [isDonorTypeMatch, setIsDonorTypeMatch] = useState(false);
  const [isInQuarantine, setIsInQuarantine] = useState(false);
  const [isAppointmentReschedule, setIsAppointmentReschedule] = useState(false);
  const [isDonorStatusPending, setIsDonorStatusPending] = useState(false);

  useEffect(() => {
    if (userPnr) {
      getUserByPnr(userPnr);
    }
  }, [getUserByPnr, userPnr]);

  useEffect(() => {
    setCurrentUser(userByPnr ? userByPnr : null);
  }, [userByPnr]);

  useEffect(() => {
    if (currentUser) {
      setIsTiterMatch(
        selectedTimeslot.bloodTiter &&
          currentUser?.status !== "new" &&
          !selectedTimeslot.bloodTiter.find(
            (item) => item === currentUser?.bloodTiter,
          ),
      );
      setIsDonorTypeMatch(
        currentUser?.status !== "new" &&
          !selectedTimeslot.donorType.find(
            (item) =>
              item?.rhesus === currentUser?.donorType?.rhesus &&
              item?.bloodType === currentUser?.donorType?.bloodType,
          ),
      );
      setIsInQuarantine(
        currentUser && currentUser.quarantine && selectedTimeslot
          ? currentUser.quarantine > selectedTimeslot.startDate
          : false,
      );
      setIsDonorStatusPending(currentUser?.status === "pending");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  useEffect(() => {
    const quarantineDate = new Date(currentUser?.quarantine);
    const currentDate = new Date(date);

    setIsInQuarantine(
      currentUser && currentUser.quarantine && currentDate
        ? quarantineDate > currentDate
        : false,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, date]);

  useEffect(() => {
    setCurrentAppointments(
      selectedTimeslot?.appointments
        .filter((item) => item.status === "pending")
        .map((item) => {
          return {
            label: `${dateFns.format(new Date(item.startDate), "HH:mm")} - ${dateFns.format(new Date(item.endDate), "HH:mm")}`,
            value: String(item.startDate),
          };
        }) || [],
    );
  }, [selectedTimeslot]);

  const handleSubmit = async () => {
    if (currentUser?.nextAppointment !== undefined) {
      setIsAppointmentReschedule(true);
      return;
    }

    bookAppointment(
      chosenAppointment.value,
      selectedTimeslot.timeslotId,
      currentUser.id,
      bloodBankId,
    );
    toggleBookAppointmentModal();
  };

  const handleSubmitOverwrite = async () => {
    startOverwriteTimeslot();
    toggleBookAppointmentModal();
    if (currentUser?.nextAppointment !== undefined) {
      await cancelAppointmentBooking(
        bloodBankId,
        currentUser.nextAppointment.reservationId,
        currentUser.id,
        currentUser.nextAppointment.startDate,
        bloodBankName,
      );
    }

    bookAppointment(
      chosenAppointment.value,
      selectedTimeslot.timeslotId,
      currentUser.id,
      bloodBankId,
    );
    setIsAppointmentReschedule(false);
    finishOverwriteTimeslot();
  };

  const handleModalClose = () => {
    toggleBookAppointmentModal();
  };

  useEffect(() => {
    const initialChosenAppointmentValue = isDayPage ? currentAppointments.find(appointment => Number(appointment.value) === currentAppointment?.startDate) : null;
    
    setUserPnr("");
    setCurrentUser(null);
    setIsTiterMatch(false);
    setIsDonorTypeMatch(false);
    setIsInQuarantine(false);
    if (!bookAppointmentModalIsOpen) {
      setChosenAppointment(initialChosenAppointmentValue);
    } else {
      if (currentAppointments.length === 1) {
        setChosenAppointment(currentAppointments[0]);
      }
    }
  }, [bookAppointmentModalIsOpen, currentAppointments, currentAppointment?.startDate, isDayPage]);

  const formatText = (text) => {
    const firstPart = text.slice(0, 6);
    const secondPart = text.slice(6, 11);
    return `${firstPart}\u00A0${secondPart}`;
  };

  const handleCopy = (e) => {
    e.clipboardData.setData("text/plain", userPnr);
    e.preventDefault();
  };

  const handleOverwrite = async () => {
    startOverwriteTimeslot();
    toggleBookAppointmentModal();
    const timeSlotForOwerride = prepareTimeslotForUpdating(selectedTimeslot);

    let newAppointmentType;
    let newBloodTiter;
    let newDonorType;

    if (currentUser?.type === "full_blood") {
      if (currentUser?.status === "new") {
        newAppointmentType = appointmentTypes.find(
          (el) => el.donorStatus === "new",
        )?.appointmentTypeId;
        newBloodTiter = [...timeSlotForOwerride?.bloodTiter];
        newDonorType = [...timeSlotForOwerride?.donorType];
      } else if (currentUser?.status !== "new") {
        if (currentUser?.status === "established") {
          newAppointmentType = appointmentTypes.find(
            (el) =>
              el.donorStatus === "established" && el.name === `N'th time draw`,
          )?.appointmentTypeId;
        } else if (currentUser?.status === "approved") {
          newAppointmentType = appointmentTypes.find(
            (el) =>
              (el.donorStatus === "approved" && el.name === `First time draw`),
          )?.appointmentTypeId;
        } else if (currentUser?.status === "pending") {
          newAppointmentType = appointmentTypes.find(
            (el) => el.donorStatus === "pending" && el.name === "Consultation"
          )?.appointmentTypeId;
        }
        newBloodTiter = [...timeSlotForOwerride?.bloodTiter];
        newDonorType = [...timeSlotForOwerride?.donorType];
      }
    } else {
      newAppointmentType = appointmentTypes.find(
        (el) =>
          el.donorStatus === currentUser?.status &&
          el.donorType === currentUser?.type,
      )?.appointmentTypeId;
      newBloodTiter = [...timeSlotForOwerride?.bloodTiter];
      newDonorType = [...timeSlotForOwerride?.donorType];
    }

    const startOverwriteDate = +chosenAppointment.value;
    const endOverwriteDate =
      startOverwriteDate +
      appointmentTypes.find((el) => el.appointmentTypeId === newAppointmentType)
        ?.duration;
    const exception = {
      from: timestampToIso(startOverwriteDate),
      to: timestampToIso(endOverwriteDate),
      date: timestampToIso(selectedTimeslot.partStart),
    };
    const updatedException = {
      ...timeSlotForOwerride,
      exceptionHoursOnDates: [
        ...timeSlotForOwerride.exceptionHoursOnDates,
        exception,
      ],
      ...(recurrenceRule && { rule: recurrenceRule }),
    };
    // console.log("newAppointmentType", newAppointmentType)
    const timeSlotForCreation = prepareTimeslotForCreation(
      {
        appointmentTypeId: newAppointmentType,
        donorType: newDonorType,
        bloodTiter: newBloodTiter,
        startDate: startOverwriteDate,
        endDate:
          startOverwriteDate +
          appointmentTypes.find(
            (el) => el.appointmentTypeId === newAppointmentType,
          )?.duration,
      },
      selectedTimeslot,
    );

    if (currentUser?.nextAppointment !== undefined) {
      await cancelAppointmentBooking(
        bloodBankId,
        currentUser.nextAppointment.reservationId,
        currentUser.id,
        currentUser.nextAppointment.startDate,
        bloodBankName,
      );
    }

    overwriteTimeslot(
      updatedException,
      timeSlotForCreation,
      bloodBankId,
      currentUser?.id,
      chosenAppointment.value,
    );
  };

  const isOverwriteDisabled =
    currentUser === null ||
    chosenAppointment === null ||
    isInQuarantine;

  const isBookDisabled = () => {
    if (isDonorStatusPending) {
      return (
        currentUser === null ||
        currentUser?.status !== selectedTimeslot?.appointmentType.donorStatus ||
        chosenAppointment === null ||
        isInQuarantine
      );
    }

    return (
      currentUser === null ||
      currentUser?.status !== selectedTimeslot?.appointmentType.donorStatus ||
      chosenAppointment === null ||
      isTiterMatch ||
      isDonorTypeMatch ||
      isInQuarantine
    );
  };

  return (
    <>
      <Modal
        isModalOpen={bookAppointmentModalIsOpen}
        handleModalClose={handleModalClose}
        {...props}
      >
        <div className="modal-header">
          <h2 className="modal-title">
            {t("configuration.settings.bookAppointmentModal.header")}
          </h2>
          <button className="modal-close" onClick={handleModalClose}>
            <span className="icon-close" />
          </button>
        </div>

        <div className="modal-body modal-content">
          <div className="form-group d-flex items-center">
            <div className="flex-grow-0 modal-label">
              {t("configuration.settings.bookAppointmentModal.id")}
            </div>
            <div className="flex-grow-1">
              <Search
                type="text"
                placeholder={t(
                  "configuration.settings.bookAppointmentModal.searchPlaceholder",
                )}
                shortSearch={true}
                setValue={setUserPnr}
                autoComplete={"off"}
                isPnrSearch={true}
              />
            </div>
          </div>

          <div className={`form-group modal-text ${currentUser || "thin"}`}>
            {currentUser ? (
              `${currentUser.firstname} ${currentUser.lastname}`
            ) : userPnr === "" ? (
              ""
            ) : (
              <>
                <span>
                  {t("configuration.settings.bookAppointmentModal.noMatch")}
                </span>
                <span onCopy={handleCopy}>{` ${formatText(userPnr)}`}</span>
              </>
            )}
          </div>

          {currentAppointments.length > 1 && currentUser && (
            <div className="form-group">
              <CustomSelect
                label={t(
                  "configuration.settings.bookAppointmentModal.selectAppointmentTime",
                )}
                data={currentAppointments}
                value={chosenAppointment}
                onChange={setChosenAppointment}
                isSearchable={true}
              />
            </div>
          )}

          {isInQuarantine ? (
            <div className="form-group modal-text thin d-flex justify-content-between">
              {t("configuration.settings.bookAppointmentModal.inQuarantine")}
            </div>
          ) : currentUser &&
          currentUser?.status !==
            selectedTimeslot?.appointmentType.donorStatus ? (
            <div className="form-group modal-text thin d-flex justify-content-between">
              {t("configuration.settings.bookAppointmentModal.statusNotMatch")}
              <Button
                disabled={isOverwriteDisabled}
                onClick={toggleOverwriteModal}
                className="btn-flat"
                text={` +${t("configuration.settings.add")}`}
              />
            </div>
          ) : currentUser?.nextAppointment ? (
            <div className="form-group modal-text thin">
              {t("configuration.settings.bookAppointmentModal.nextAppointment")}
            </div>
          ) : isTiterMatch && !isDonorStatusPending ? (
            <div className="form-group modal-text thin d-flex justify-content-between">
              {t("configuration.settings.bookAppointmentModal.titerNotMatch")}
              <Button
                disabled={isOverwriteDisabled}
                onClick={toggleOverwriteModal}
                className="btn-flat"
                text={` +${t("configuration.settings.add")}`}
              />
            </div>
          ) : isDonorTypeMatch && !isDonorStatusPending? (
            <div className="form-group modal-text thin d-flex justify-content-between">
              {t(
                "configuration.settings.bookAppointmentModal.donorTypeNotMatch",
              )}
              <Button
                disabled={isOverwriteDisabled}
                onClick={toggleOverwriteModal}
                className="btn-flat"
                text={` +${t("configuration.settings.add")}`}
              />
            </div>
          ) : (
            <></>
          )}
        </div>

        <div className="modal-footer">
          <div className="inputs-group">
            <div className="inputs-group-item">
              <Button
                type="button"
                text={t("common.cancel")}
                onClick={toggleBookAppointmentModal}
              />
            </div>
            <div className="inputs-group-item">
              <Button
                disabled={isBookDisabled()}
                btnClassName="btn-primary"
                type="button"
                text={t("common.book")}
                onClick={handleSubmit}
              />
            </div>
          </div>
        </div>
      </Modal>
      <OverWriteModal
        isModalOpen={isOverwriteModalOpened}
        handleClose={toggleOverwriteModal}
        onApply={handleOverwrite}
        isInProgress={isOverwriting}
        isError={overwritingError}
      />
      <OverWriteModal
        isModalOpen={isAppointmentReschedule}
        handleClose={() => setIsAppointmentReschedule(false)}
        onApply={handleSubmitOverwrite}
      />
    </>
  );
};

export default BookAppointment;
